const youtubeIframeApiUrl = 'https://www.youtube.com/iframe_api';
const youtubeApiScriptId = 'youtube-iframe-api';
const progressInterval = 250;
const seekTolerance = 2;
const quartiles = [25, 50, 75];

let youtubeApiPromise = null;

/**
 * Loads the YouTube IFrame API once and preserves an existing global ready callback.
 * @returns {Promise} Resolves with the YouTube API namespace.
 */
export const loadYoutubeIframeApi = () => {
  if (window.YT && typeof window.YT.Player === 'function') {
    return Promise.resolve(window.YT);
  }

  if (youtubeApiPromise) {
    return youtubeApiPromise;
  }

  youtubeApiPromise = new Promise((resolve, reject) => {
    const existingReadyCallback = window.onYouTubeIframeAPIReady;
    let script = document.getElementById(youtubeApiScriptId)
      || document.querySelector(`script[src="${youtubeIframeApiUrl}"]`);

    window.onYouTubeIframeAPIReady = () => {
      if (typeof existingReadyCallback === 'function') {
        existingReadyCallback();
      }

      resolve(window.YT);
    };

    if (!script) {
      script = document.createElement('script');
      script.id = youtubeApiScriptId;
      script.src = youtubeIframeApiUrl;
      script.async = true;
      document.head.appendChild(script);
    }

    script.addEventListener('error', () => {
      youtubeApiPromise = null;
      reject(new Error('YouTube IFrame API could not be loaded'));
    }, { once: true });
  });

  return youtubeApiPromise;
};

/**
 * Translates YouTube IFrame API events into the same GA4 video events used by
 * the JW Player integration. YouTube does not expose JW-style `time` or `seek`
 * events, so playback progress and forward seeks are derived by polling.
 */
export class YoutubeVideoAnalytics {
  /**
   * Creates analytics state for one popup iframe and connects it to YT.Player.
   * @param {HTMLIFrameElement} iframe - YouTube iframe created by Magnific Popup.
   * @param {Object} options - Analytics metadata for the selected video.
   * @param {String} options.videoUrl - Original public YouTube URL.
   * @param {String} options.videoTitle - CMS title used until YouTube metadata is ready.
   */
  constructor(iframe, options) {
    this.iframe = iframe;
    this.videoUrl = options.videoUrl;
    this.fallbackTitle = options.videoTitle || 'Video';
    this.player = null;
    this.progressIntervalId = null;
    this.startFired = false;
    this.sessionEnded = false;
    this.firedQuartiles = { 25: false, 50: false, 75: false };
    this.lastPosition = null;
    this.lastSampleTime = null;

    this.init();
  }

  /**
   * Wraps the existing iframe with the YouTube API and registers lifecycle listeners.
   * The iframe already contains `enablejsapi=1`, which allows this connection.
   */
  init() {
    this.player = new window.YT.Player(this.iframe, {
      events: {
        onReady: event => this.handleReady(event),
        onStateChange: event => this.handleStateChange(event)
      }
    });
  }

  /**
   * Handles the race where autoplay starts before the state-change listener is ready.
   * @param {Object} event - YouTube onReady event.
   */
  handleReady(event) {
    if (event.target.getPlayerState() === window.YT.PlayerState.PLAYING) {
      this.handlePlaying();
    }
  }

  /**
   * Starts polling only while playback is active and stops it for pause, buffering,
   * cueing or completion. Completion is handled separately to send the 100% event.
   * @param {Object} event - YouTube onStateChange event.
   */
  handleStateChange(event) {
    if (event.data === window.YT.PlayerState.PLAYING) {
      this.handlePlaying();

      return;
    }

    this.stopProgressTracking();

    if (event.data === window.YT.PlayerState.ENDED) {
      this.handleComplete();
    }
  }

  /**
   * Starts a new analytics session after replay, sends start when metadata is ready,
   * and begins progress polling. Resume does not create another start event.
   */
  handlePlaying() {
    if (this.sessionEnded) {
      this.resetSession();
    }

    this.fireStart();
    this.startProgressTracking();
  }

  /**
   * Sends one start event per playback session. YouTube initially reports duration
   * as zero, so the event is deferred until video metadata becomes available.
   */
  fireStart() {
    const duration = this.getDuration();

    if (this.startFired || duration <= 0) {
      return;
    }

    this.pushEvent('ga4_video_start', 0, duration, 0);
    this.startFired = true;
  }

  /**
   * Starts the lightweight timer used to emulate JW Player's `time` event.
   * Initial position and wall-clock time are retained for forward-seek detection.
   */
  startProgressTracking() {
    if (this.progressIntervalId) {
      return;
    }

    this.lastPosition = this.getCurrentTime();
    this.lastSampleTime = Date.now();
    this.progressIntervalId = window.setInterval(() => this.trackProgress(), progressInterval);
  }

  /**
   * Stops progress work when the video is not playing and clears sample history so
   * time spent paused is never mistaken for normal playback or a seek.
   */
  stopProgressTracking() {
    if (this.progressIntervalId) {
      window.clearInterval(this.progressIntervalId);
      this.progressIntervalId = null;
    }

    this.lastPosition = null;
    this.lastSampleTime = null;
  }

  /**
   * Sends each 25/50/75 percent event once. Because YouTube has no seek event, the
   * actual playhead movement is compared with elapsed time and playback rate. A
   * larger jump is treated as a forward seek: crossed quartiles are marked as fired
   * without sending them, matching the existing JW Player behavior.
   */
  trackProgress() {
    const duration = this.getDuration();
    const currentTime = this.getCurrentTime();
    const sampleTime = Date.now();

    if (duration <= 0) {
      return;
    }

    this.fireStart();

    const percentage = currentTime * 100 / duration;
    const elapsed = this.lastSampleTime ? (sampleTime - this.lastSampleTime) / 1000 : 0;
    const playbackRate = this.getPlaybackRate();
    const expectedAdvance = elapsed * playbackRate;
    const jumpedForward = this.lastPosition !== null
      && currentTime - this.lastPosition > expectedAdvance + seekTolerance;

    quartiles.forEach(quartile => {
      if (percentage >= quartile && !this.firedQuartiles[quartile]) {
        if (!jumpedForward) {
          this.pushEvent('ga4_video_progress', currentTime, duration, quartile);
        }

        this.firedQuartiles[quartile] = true;
      }
    });

    this.lastPosition = currentTime;
    this.lastSampleTime = sampleTime;
  }

  /**
   * Sends completion once for the current session. State is reset only when YouTube
   * enters PLAYING again, preventing duplicate ENDED notifications from duplicating GA4.
   */
  handleComplete() {
    if (this.sessionEnded) {
      return;
    }

    const duration = this.getDuration();

    this.pushEvent('ga4_video_complete', duration, duration, 100);
    this.sessionEnded = true;
  }

  /**
   * Prepares the same player instance for a manual replay.
   */
  resetSession() {
    this.startFired = false;
    this.sessionEnded = false;
    this.firedQuartiles = { 25: false, 50: false, 75: false };
    this.lastPosition = null;
    this.lastSampleTime = null;
  }

  /**
   * Safely reads the current playhead position while the API is being initialized.
   * @returns {Number} Current playback time in seconds.
   */
  getCurrentTime() {
    return this.player && typeof this.player.getCurrentTime === 'function'
      ? this.player.getCurrentTime()
      : 0;
  }

  /**
   * Safely reads duration, which can be zero before YouTube metadata is loaded.
   * @returns {Number} Video duration in seconds.
   */
  getDuration() {
    return this.player && typeof this.player.getDuration === 'function'
      ? this.player.getDuration()
      : 0;
  }

  /**
   * Reads playback speed so faster or slower playback is not mistaken for seeking.
   * @returns {Number} Current playback-rate multiplier.
   */
  getPlaybackRate() {
    return this.player && typeof this.player.getPlaybackRate === 'function'
      ? this.player.getPlaybackRate()
      : 1;
  }

  /**
   * Prefers the canonical YouTube title and falls back to metadata from the CMS trigger.
   * @returns {String} Title included in the GA4 payload.
   */
  getVideoTitle() {
    if (this.player && typeof this.player.getVideoData === 'function') {
      const videoData = this.player.getVideoData();

      if (videoData && videoData.title) {
        return videoData.title;
      }
    }

    return this.fallbackTitle;
  }

  /**
   * Recreates JW Player's getViewable() semantics: the active tab and at least half
   * of the iframe must be visible. The GA4 contract expects numeric 1 or 0.
   * @returns {Number} One when viewable, otherwise zero.
   */
  isViewable() {
    const iframe = this.player && typeof this.player.getIframe === 'function'
      ? this.player.getIframe()
      : this.iframe;

    if (!iframe || document.visibilityState !== 'visible') {
      return 0;
    }

    const rect = iframe.getBoundingClientRect();
    const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    const playerArea = rect.width * rect.height;

    return playerArea > 0 && visibleWidth * visibleHeight / playerArea >= 0.5 ? 1 : 0;
  }

  /**
   * Builds the existing GA4 video contract and appends it directly to dataLayer.
   * @param {String} event - GA4 event name.
   * @param {Number} currentTime - Current playback time in seconds.
   * @param {Number} duration - Total video duration in seconds.
   * @param {Number} percentage - Start, quartile or completion percentage.
   */
  pushEvent(event, currentTime, duration, percentage) {
    window.dataLayer = window.dataLayer || [];
    /* eslint-disable camelcase */
    window.dataLayer.push({
      event,
      ga4_data: {
        video_current_time: Math.floor(currentTime),
        video_duration: Math.floor(duration),
        video_percent: percentage,
        video_provider: 'YouTube',
        video_title: this.getVideoTitle(),
        video_url: this.videoUrl,
        visible: this.isViewable()
      }
    });
    /* eslint-enable camelcase */
  }

  /**
   * Releases timers and YouTube resources when Magnific Popup closes.
   */
  destroy() {
    this.stopProgressTracking();

    if (this.player && typeof this.player.destroy === 'function') {
      this.player.destroy();
    }

    this.player = null;
  }
}
